import * as THREE from 'three'

/**
 * 特征拓扑显示边 GPU 资源工厂。
 *
 * 普通 `FeatureTopologyDisplayEdgesMesh` 与 imported-model 实例边线必须使用完全相同的
 * BRep 拓扑折线规则；本工厂只构建 Geometry/Material，不持有场景对象和释放职责。
 */
export class FeatureTopologyDisplayEdgesResourceFactory {
  /** 默认显示边材质名称。 */
  private static readonly DEFAULT_MATERIAL_NAME = '特征拓扑显示边-默认材质'

  /**
   * 根据一个或多个拓扑快照构建合并线段几何。
   *
   * @param snapshots 权威拓扑快照集合。
   * @returns 每两个 position 顶点表达一条独立线段的合并几何。
   */
  public static createGeometry(
    snapshots: readonly TopologySnapshot[],
  ): THREE.BufferGeometry {
    const positions: number[] = []
    snapshots.forEach((snapshot) => {
      const vertexMap = this.buildTopologyVertexMap(snapshot.vertices)
      snapshot.edges.forEach((edge) => {
        const polyline = this.resolveDisplayEdgePolyline(edge, vertexMap)
        if (!polyline) {
          return
        }
        // LineSegments 需要按线段成对写入顶点，曲线边在这里展开为连续小线段。
        for (let index = 0; index < polyline.length - 1; index += 1) {
          this.pushTopologyPoint(positions, polyline[index]!)
          this.pushTopologyPoint(positions, polyline[index + 1]!)
        }
      })
    })

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()
    return geometry
  }

  /**
   * 创建默认拓扑显示边材质。
   *
   * @param color 显示边颜色。
   * @returns 由调用方独占并负责释放的线材质。
   */
  public static createMaterial(
    color: THREE.ColorRepresentation,
  ): THREE.LineBasicMaterial {
    const material = new THREE.LineBasicMaterial({
      color,
      transparent: false,
      opacity: 1,
      depthTest: true,
      depthWrite: false,
    })
    material.name = this.DEFAULT_MATERIAL_NAME
    return material
  }

  /**
   * 构建拓扑顶点索引表。
   *
   * @param vertices 拓扑顶点列表。
   * @returns 以拓扑 index 为键的顶点表。
   */
  private static buildTopologyVertexMap(
    vertices: readonly TopologySnapshotVertex[],
  ): Map<number, TopologySnapshotVertex> {
    const vertexMap = new Map<number, TopologySnapshotVertex>()
    vertices.forEach((vertex) => {
      vertexMap.set(vertex.index, vertex)
    })
    return vertexMap
  }

  /**
   * 解析边应渲染的折线点。
   *
   * @param edge 拓扑边快照。
   * @param vertexMap 顶点索引表。
   * @returns 曲线采样点或直线端点；信息不足时返回 null。
   */
  private static resolveDisplayEdgePolyline(
    edge: TopologySnapshotEdge,
    vertexMap: ReadonlyMap<number, TopologySnapshotVertex>,
  ): readonly TopologyPoint3D[] | null {
    if (this.hasRenderablePolyline(edge.sampledPoints)) {
      return edge.sampledPoints
    }

    const vertexA = vertexMap.get(edge.vertexAIndex)
    const vertexB = vertexMap.get(edge.vertexBIndex)
    if (!vertexA || !vertexB) {
      return null
    }
    return [vertexA.position, vertexB.position]
  }

  /**
   * 判断拓扑点数组是否能构成至少一条线段。
   *
   * @param points 拓扑点数组。
   * @returns 可以构成线段时返回 true。
   */
  private static hasRenderablePolyline(
    points: readonly TopologyPoint3D[] | undefined,
  ): points is readonly TopologyPoint3D[] {
    return Array.isArray(points) && points.length > 1
  }

  /**
   * 将拓扑点写入线段 position 数组。
   *
   * @param target 目标坐标数组。
   * @param point 拓扑点。
   */
  private static pushTopologyPoint(target: number[], point: TopologyPoint3D): void {
    target.push(point[0], point[1], point[2])
  }
}
